import React, { useCallback, useEffect, useState } from "react";
import { listPeriodLocks, lockPeriod, unlockPeriod } from "../api";
import { Card, Field, Select, Loading, ErrorState, EmptyState } from "./ui";
import { fmtDate, fmtPeriod } from "../lib/format";

/**
 * Sign-off locks.
 *
 * Deltek keeps restating history, and the reports now arrive on their own, so
 * without this a nightly import could quietly rewrite a month that has already
 * been reported on. A locked period is skipped entirely by every import: its
 * rows are neither deleted nor rewritten.
 *
 * Locks are per table, so June 2025 can be closed for expenses while labour is
 * still moving.
 */
const TABLE_LABELS = {
  expenses: "Expenses",
  co_expenses: "Revenue",
  labor_entries: "Labour hours",
};

export default function PeriodLocks() {
  const [data, setData] = useState({ locks: [], tables: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [table, setTable] = useState("");
  const [period, setPeriod] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    listPeriodLocks()
      .then((d) => {
        setData(d);
        setTable((t) => t || d.tables[0] || "");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      // A month input gives YYYY-MM; the tables store MM/YYYY.
      const [y, m] = period.split("-");
      await lockPeriod(table, `${m}/${y}`, note);
      setNote("");
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (lock) => {
    if (!window.confirm(
      `Unlock ${fmtPeriod(lock.period)} for ${TABLE_LABELS[lock.table_name] || lock.table_name}?`
      + `\n\nThe next import will be free to replace that period again.`
    )) return;
    setError("");
    try {
      await unlockPeriod(lock.id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Loading label="Loading locks…" />;
  if (error && !data.locks.length) return <ErrorState message={error} onRetry={load} />;

  return (
    <Card
      title="Locked periods"
      note="a signed-off month an import may not touch"
      flush
    >
      <form className="dept-add" onSubmit={add}>
        <Field label="Data">
          <Select
            value={table}
            onChange={setTable}
            options={data.tables.map((t) => ({ value: t, label: TABLE_LABELS[t] || t }))}
            style={{ minWidth: 150 }}
          />
        </Field>
        <Field label="Month">
          <input
            className="input"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </Field>
        <Field label="Note">
          <input
            className="input"
            type="text"
            placeholder="signed off, reported to partners…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ minWidth: 200 }}
          />
        </Field>
        <button className="btn btn--primary" type="submit" disabled={busy || !table || !period}>
          {busy ? "Locking…" : "Lock month"}
        </button>
      </form>

      {error && <div className="notice notice--bad">{error}</div>}

      <p className="toolbar-note dept-hint">
        A locked month is skipped by every import — including the automatic ones —
        so a later Deltek export restating it cannot move a number you have already
        reported. Rows in a locked month are neither deleted nor rewritten. Only
        data that carries a period can be locked; receivables and unbilled have no
        period, so they are not listed.
      </p>

      {!data.locks.length ? (
        <EmptyState
          title="Nothing locked"
          hint="Every period is open to being replaced by the next import."
        />
      ) : (
        <div className="tbl-wrap tbl-wrap--flush">
          <table className="tbl tbl-zebra">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Data</th>
                <th style={{ width: 120 }}>Month</th>
                <th className="col-flex">Note</th>
                <th style={{ width: 90 }}>Locked by</th>
                <th style={{ width: 130 }}>Locked at</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {data.locks.map((l) => (
                <tr key={l.id}>
                  <td>{TABLE_LABELS[l.table_name] || l.table_name}</td>
                  <td><span className="code-tag">{fmtPeriod(l.period)}</span></td>
                  <td className="truncate" title={l.note || ""}>
                    {l.note || <span className="dash">—</span>}
                  </td>
                  <td><span className="badge">{String(l.locked_by || "").toUpperCase()}</span></td>
                  <td className="muted">{l.locked_at ? fmtDate(l.locked_at) : "—"}</td>
                  <td>
                    <button className="btn btn--sm btn--danger" onClick={() => remove(l)}>
                      Unlock
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
